import * as Sentry from '@sentry/nextjs';
import { ETHENA_SUPPORT_EMAIL } from 'lib/constants';
import { EnvVariable, getEnv } from 'lib/helpers/envHelper';
import _ from 'lodash';
import { NextPageContext } from 'next';
import { parse, ParsedUrlQuery } from 'querystring';
import { v4 as uuidv4 } from 'uuid';
import { redirect } from '../redirect';
import { getServerContext } from '../serverContext';
import { UnauthorizedErrorType } from './types';

enum HeaderName {
  CSRF_TOKEN = 'x-csrf-token',
}

export type ErrorType = UnauthorizedErrorType | null;

export interface IFetchResponse<T> {
  data: T;
  aborted: boolean;
  error: boolean;
  errorType?: ErrorType;
  headers: Record<string, string[]>;
  message: string;
  status: number;
  unauthorized: boolean;
}

export interface ISafeFetchConfigOptions {
  /**
   * CSRF token to be sent with the request.
   */
  csrfToken?: string;

  /**
   * Default behaviour sets the Content-Type header to 'application/json'.
   * This option allows you to bypass that behaviour.
   */
  dontSetContentType?: boolean;

  /**
   * Default behaviour redirects to login for requests that return a 401 status code.
   * This option allows you to bypass that behaviour.
   */
  dontRedirect?: boolean;

  /**
   * Default behaviour redirects to login for requests that return a 401 status code.
   * This option allows you to bypass that behaviour for specific error types.
   */
  dontRedirectForErrorTypes?: ErrorType[];

  /**
   * Default behaviour redirects to auth0 login for requests that return a 401 status code.
   * This option allows you to customize the redirect path. (i.e., the learning center should
   * redirect to the learning center login page)
   */
  buildUnauthorizedRedirectPath?: (data: {
    asPath: string;
    errorType: UnauthorizedErrorType;
  }) => string | null;

  /**
   * This option lets you set headers when safeFetch is called client side. You can use
   * `window`, `sessionStorage`, `localStorage`, etc. in this function.
   */
  clientHeaders?: () => Record<string, string>;

  /**
   * This option lets you set headers when safeFetch is called when server-side rendering. You can only
   * use the NextPageContext here.
   */
  serverHeaders?: (ctx: NextPageContext) => Record<string, string>;
}

/**
 * Get the current path of the request, taking into account the executing environment (server or client).
 */
const getCurrentPath = (serverContext: NextPageContext): string => {
  return serverContext
    ? serverContext.asPath
    : `${window.location.pathname}${window.location.search}`;
};

/**
 * Get the host of the request, taking into account the executing environment (server or client).
 */
const getHost = (serverContext: NextPageContext): string => {
  return serverContext
    ? (serverContext.req.headers['x-forwarded-host'] as string)
    : window.location.host;
};

/**
 * Get the URL query parameters, taking into account the executing environment (server or client).
 */
const getQuery = (serverContext: NextPageContext): ParsedUrlQuery => {
  return serverContext
    ? serverContext.query
    : parse(window.location.search.slice(1));
};

export const safeFetch = async <T = any>(
  url: string,
  fetchOptions?: RequestInit,
  configOptions?: ISafeFetchConfigOptions
): Promise<IFetchResponse<T>> => {
  const csrfToken = configOptions?.csrfToken ?? undefined;
  const dontSetContentType = configOptions?.dontSetContentType ?? false;
  const dontRedirect = configOptions?.dontRedirect ?? false;

  const serverContext = getServerContext();
  const currentPath = getCurrentPath(serverContext);
  const host = getHost(serverContext);
  const query = getQuery(serverContext);

  const requestId = uuidv4();
  const result: IFetchResponse<T> = {
    aborted: false,
    data: null,
    error: false,
    headers: {},
    message: '',
    status: null,
    unauthorized: false,
  };

  // #############################################################################
  // Figuring out headers and other options so we can fetch from server or browser
  // #############################################################################
  try {
    let options = {
      method: 'GET', // by default
      ...fetchOptions,
    };
    if (serverContext) {
      // it's server side
      options = {
        ...options,
        headers: {
          ..._.omit(serverContext.req.headers, ['host', 'connection']),
          host,
          ...configOptions?.serverHeaders?.(serverContext),
        },
      };
    } else {
      // we are client side
      options = {
        ...options,
        credentials: 'include',
        headers: {
          [HeaderName.CSRF_TOKEN]: csrfToken,
          ...configOptions?.clientHeaders?.(),
        },
      };
    }
    // set content type to json by default for client and server
    const contentType = dontSetContentType
      ? {}
      : {
          'Content-Type': 'application/json',
        };
    options = {
      ...options,
      headers: {
        ...options.headers,
        ...contentType,
        // Append a X-Request-ID header in order to link BE + FE errors.
        'X-Request-ID': requestId,
        // Append the current application version for better logging in Sentry.
        'X-Application-Version': getEnv(EnvVariable.NPM_PACKAGE_VERSION),
      },
    };

    // #############################################################################
    // Actually doing the request itself LFGGGGGGG
    // #############################################################################
    const serverResponse = await fetch(
      `${getEnv(EnvVariable.PROTOCOL)}${host}${getEnv(
        EnvVariable.API_PATHNAME
      )}${url}`,
      options
    );
    if (!serverResponse) {
      // we are probably exiting the page, this tends to throw and be forgotten
      return null;
    }

    result.status = serverResponse.status;

    // Set cookies appropriately on the client when requests are made on the server.
    if (serverContext) {
      serverContext.res.setHeader(
        'Set-Cookie',
        serverResponse.headers.getSetCookie()
      );
    }

    serverResponse.headers.forEach((value, key) => {
      if (result.headers[key]) {
        result.headers[key].push(value);
      } else {
        result.headers[key] = [value];
      }
    });

    // Handle 'application/json' content type.
    const responseContentType = serverResponse.headers.get('Content-Type');
    const isJson = responseContentType?.includes('application/json');
    if (isJson) {
      const json = await serverResponse.json();

      if (json.success) {
        result.data = json.data;
      } else {
        result.error = true;
        result.errorType = json.errorType;
        result.message = json.message;
        result.data = json.data; // sometimes we still pass data back
      }
    } else {
      // Handle any other content type.
      const blob = await serverResponse.blob();
      result.data = blob as unknown as T;
    }

    // #############################################################################
    // If we get a 401, we should redirect them to login and try and land them back
    // in the same URL they were already on
    // #############################################################################
    if (result.status === 401) {
      result.error = true;
      result.unauthorized = true;
      result.message = result.message || 'Unauthorized';

      if (dontRedirect) {
        return result;
      }

      if (
        _.includes(configOptions?.dontRedirectForErrorTypes, result.errorType)
      ) {
        return result;
      }

      const ssoParam = query && query.sso ? `&sso=${query.sso}` : '';
      const customRedirect = configOptions?.buildUnauthorizedRedirectPath?.({
        asPath: currentPath,
        errorType: result.errorType,
      });

      if (customRedirect) {
        console.log('401 redirect to: ', customRedirect);
        redirect(customRedirect);
      } else {
        console.log('401 redirect to: ', currentPath);
        redirect(
          `${getEnv(
            EnvVariable.API_PATHNAME
          )}/login?redirectPath=${encodeURIComponent(currentPath)}${ssoParam}`
        );
      }
    } else if (result.status === 403) {
      result.error = true;
      result.unauthorized = true;
      result.message = result.message || 'Forbidden';
    } else if (result.status === 404) {
      result.error = true;
      result.message = result.message || 'Not found';
    }

    return result;
  } catch (e) {
    // Request was aborted, we can ignore this error.
    if (e.name === 'AbortError') {
      result.aborted = true;
      return result;
    }

    // Capture all errors that might arise during fetch setup and execution.
    Sentry.withScope((scope) => {
      scope.setTag('request_id', requestId);
      Sentry.captureException(e);
    });
    // failed or something, let's return null
    result.error = true;
    result.message = `Something went wrong. Contact ${ETHENA_SUPPORT_EMAIL} if this problem persists.`;
    return result;
  }
};

export default safeFetch;
